'use client';

import type { ReactNode } from 'react';
import type { PageBlock } from '@impact/types';
import { RichtextBlockRenderer } from './RichtextBlock';
import { HeadingBlockRenderer } from './HeadingBlock';
import { ImageBlockRenderer } from './ImageBlock';
import { GalleryBlockRenderer } from './GalleryBlock';
import { CarouselBlockRenderer } from './CarouselBlock';
import { VideoBlockRenderer } from './VideoBlock';
import { FAQBlockRenderer } from './FAQBlock';
import { ContactFormBlockRenderer } from './ContactFormBlock';
import { CTABlockRenderer } from './CTABlock';
import { StatsBlockRenderer } from './StatsBlock';
import { QuoteBlockRenderer } from './QuoteBlock';
import { DividerBlockRenderer } from './DividerBlock';
import { ColumnsBlockRenderer } from './ColumnsBlock';
import { SpacerBlockRenderer } from './SpacerBlock';
import { ListBlockRenderer } from './ListBlock';
import { CodeBlockRenderer } from './CodeBlock';
import { SectionBlockRenderer } from './SectionBlock';
import { ButtonBlockRenderer } from './ButtonBlock';
import type { EditableWrapper } from './types';

// Re-export for external consumers
export type { EditableWrapperProps } from './types';

export interface BlockRendererProps {
  block: PageBlock;
  editing?: boolean;
  Wrapper?: EditableWrapper;
}

/**
 * Main block renderer that delegates to specific block type renderers.
 * Accepts an optional `editing` flag (passed to every child renderer) and
 * an optional `Wrapper` component (injected by the v2 editor canvas so that
 * admin-only chrome never leaks into the shared renderer tree).
 */
export function BlockRenderer({ block, editing, Wrapper }: BlockRendererProps) {
  let node: ReactNode = null;
  switch (block.type) {
    case 'richtext':
      node = <RichtextBlockRenderer block={block} editing={editing} />;
      break;
    case 'heading':
      node = <HeadingBlockRenderer block={block} editing={editing} />;
      break;
    case 'image':
      node = <ImageBlockRenderer block={block} editing={editing} />;
      break;
    case 'gallery':
      node = <GalleryBlockRenderer block={block} editing={editing} />;
      break;
    case 'carousel':
      node = <CarouselBlockRenderer block={block} editing={editing} />;
      break;
    case 'video':
      node = <VideoBlockRenderer block={block} editing={editing} />;
      break;
    case 'faq':
      node = <FAQBlockRenderer block={block} editing={editing} />;
      break;
    case 'contact-form':
      node = <ContactFormBlockRenderer block={block} editing={editing} />;
      break;
    case 'cta':
      node = <CTABlockRenderer block={block} editing={editing} />;
      break;
    case 'stats':
      node = <StatsBlockRenderer block={block} editing={editing} />;
      break;
    case 'quote':
      node = <QuoteBlockRenderer block={block} editing={editing} />;
      break;
    case 'divider':
      node = <DividerBlockRenderer block={block} editing={editing} />;
      break;
    case 'columns':
      node = <ColumnsBlockRenderer block={block} editing={editing} Wrapper={Wrapper} />;
      break;
    case 'spacer':
      node = <SpacerBlockRenderer block={block} editing={editing} />;
      break;
    case 'list':
      node = <ListBlockRenderer block={block} editing={editing} />;
      break;
    case 'code':
      node = <CodeBlockRenderer block={block} editing={editing} />;
      break;
    case 'section':
      node = <SectionBlockRenderer block={block} editing={editing} Wrapper={Wrapper} />;
      break;
    case 'button':
      node = <ButtonBlockRenderer block={block} editing={editing} />;
      break;
  }
  return Wrapper ? <Wrapper block={block}>{node}</Wrapper> : <>{node}</>;
}

interface BlocksRendererProps {
  blocks: PageBlock[];
}

/**
 * Renders an array of blocks
 */
export function BlocksRenderer({ blocks }: BlocksRendererProps) {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  return (
    <div className="space-y-8">
      {blocks.map((block) => (
        <BlockRenderer key={block.id} block={block} />
      ))}
    </div>
  );
}

// =============================================================================
// PAGE-LEVEL BLOCKS RENDERER
// =============================================================================

interface PageBlocksRendererProps {
  blocks: PageBlock[];
}

type BlockGroup = { type: 'loose'; blocks: PageBlock[] } | { type: 'section'; block: PageBlock };

function groupBlocks(blocks: PageBlock[]): BlockGroup[] {
  const groups: BlockGroup[] = [];
  let looseBlocks: PageBlock[] = [];

  for (const block of blocks) {
    if (block.type === 'section') {
      if (looseBlocks.length > 0) {
        groups.push({ type: 'loose', blocks: looseBlocks });
        looseBlocks = [];
      }
      groups.push({ type: 'section', block });
    } else {
      looseBlocks.push(block);
    }
  }

  if (looseBlocks.length > 0) {
    groups.push({ type: 'loose', blocks: looseBlocks });
  }

  return groups;
}

/**
 * Top-level page renderer that handles section blocks with full-bleed backgrounds.
 * Wraps non-section blocks in the default container.
 * Use this instead of BlocksRenderer at the page template level.
 */
export function PageBlocksRenderer({ blocks }: PageBlocksRendererProps) {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  const groups = groupBlocks(blocks);

  return (
    <>
      {groups.map((group, index) => {
        if (group.type === 'section') {
          return <BlockRenderer key={group.block.id} block={group.block} />;
        }

        // Loose blocks wrapped in default container
        const groupKey = group.blocks[0]?.id ?? index;
        return (
          <div key={groupKey} className="py-4">
            <div className="container">
              <div className="max-w-4xl mx-auto space-y-8">
                {group.blocks.map((block) => (
                  <BlockRenderer key={block.id} block={block} />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
